import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase, isDevelopmentMode } from '../utils/supabase/client';
import { setApiSession } from '../services/apiClient';

// Mode démonstration : la session simulée est conservée localement pour survivre à un rechargement
const DEMO_SESSION_KEY = 'demo_auth_session';

// Démonstration : identifiant stable dérivé de l'email, pour retrouver ses investissements après reconnexion
export const demoUserId = (email) => {
  let hash = 5381;
  for (const char of String(email).trim().toLowerCase()) {
    hash = ((hash * 33) ^ char.charCodeAt(0)) >>> 0;
  }
  return `dev-user-${hash.toString(36)}`;
};

const readDemoSession = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(DEMO_SESSION_KEY) || 'null');
    return saved?.user && saved.expires_at > Date.now() ? saved : null;
  } catch (error) {
    return null;
  }
};

const writeDemoSession = (session) => {
  try {
    if (session) {
      localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(DEMO_SESSION_KEY);
    }
  } catch (error) {
    // Stockage indisponible (navigation privée) : la session reste en mémoire
  }
};

// Démonstration : comptes fournis (affichés sur la page de connexion) + comptes créés par l'inscription.
// Seuls ces comptes, avec leur mot de passe exact, peuvent se connecter.
export const DEMO_ACCOUNTS_KEY = 'demo_accounts';
export const BUILT_IN_DEMO_ACCOUNTS = {
  'admin@test.com': 'admin123',
  'user@test.com': 'user123',
  'demo@demo.com': 'demo123'
};

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

/** Empreinte du mot de passe (le mot de passe lui-même n'est jamais stocké) */
const hashPassword = async (email, password) => {
  const data = new TextEncoder().encode(`${normalizeEmail(email)}:${password}`);
  if (window.crypto?.subtle) {
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
  }
  // Contexte non sécurisé (http hors localhost) : empreinte simple, suffisante pour une démonstration
  let hash = 5381;
  for (const byte of data) hash = ((hash * 33) ^ byte) >>> 0;
  return `djb2-${hash.toString(16)}`;
};

export const readDemoAccounts = () => {
  try {
    return JSON.parse(localStorage.getItem(DEMO_ACCOUNTS_KEY) || '{}');
  } catch (error) {
    return {};
  }
};

export const writeDemoAccounts = (accounts) => {
  try {
    localStorage.setItem(DEMO_ACCOUNTS_KEY, JSON.stringify(accounts));
  } catch (error) {
    // Stockage indisponible : le compte ne survivra pas au rechargement
  }
};

/** Vérifie un compte de démonstration ; renvoie ses informations ou null */
const checkDemoAccount = async (email, password) => {
  const key = normalizeEmail(email);
  if (BUILT_IN_DEMO_ACCOUNTS[key] !== undefined) {
    return BUILT_IN_DEMO_ACCOUNTS[key] === password ? { metadata: {} } : null;
  }
  const account = readDemoAccounts()[key];
  if (!account) return null;
  return account.hash === await hashPassword(key, password) ? account : null;
};

const demoAccountExists = (email) => {
  const key = normalizeEmail(email);
  return BUILT_IN_DEMO_ACCOUNTS[key] !== undefined || Boolean(readDemoAccounts()[key]);
};

/**
 * 🔐 ENHANCED SUPABASE AUTH CONTEXT - Système d'authentification complet
 * ======================================================================
 *
 * ✨ Fonctionnalités avancées :
 * - Gestion complète des sessions Supabase
 * - Authentification sécurisée avec validation
 * - Cache intelligent et optimisations performance
 * - Support mode développement avec fallback
 * - Gestion d'erreurs robuste et logging
 * - Hooks personnalisés pour l'état utilisateur
 * - Nettoyage automatique des resources
 *
 * 🎯 Usage : Provider principal pour l'authentification dans l'app
 */

const SupabaseAuthContext = createContext();

// Hook personnalisé pour utiliser le context
export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};

// Alias pour compatibilité et simplicité
export const useAuth = useSupabaseAuth;

/**
 * Provider d'authentification Supabase avec fonctionnalités étendues
 */
export const SupabaseAuthProvider = ({ children }) => {
  // États principaux
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // États additionnels pour UX améliorée
  const [isInitialized, setIsInitialized] = useState(false);
  const [authAction, setAuthAction] = useState(null); // 'signin', 'signup', 'signout'

  // Le client du backend de paiement utilise le jeton de la session courante
  useEffect(() => {
    setApiSession(session);
    if (isDevelopmentMode && isInitialized) {
      writeDemoSession(session);
    }
  }, [session, isInitialized]);

  /**
   * Fonction utilitaire pour gérer les erreurs d'authentification avec messages friendly
   */
  const handleAuthError = useCallback((error, context = '') => {
    let userFriendlyMessage = 'Une erreur inattendue s\'est produite. Réessayez dans un instant.';

    // Messages clairs selon le type d'erreur (le contexte sert seulement au journal technique)
    if (error?.message) {
      const errorMsg = error.message.toLowerCase();

      if (errorMsg.includes('invalid login credentials') || errorMsg.includes('invalid_credentials') || errorMsg.includes('email ou mot de passe incorrect')) {
        userFriendlyMessage = 'Email ou mot de passe incorrect.';
      } else if (errorMsg.includes('email not confirmed') || errorMsg.includes('email_not_confirmed')) {
        userFriendlyMessage = 'Votre adresse email n\'est pas encore confirmée : cliquez sur le lien reçu par email.';
      } else if (errorMsg.includes('account_suspended')) {
        userFriendlyMessage = 'Ce compte est suspendu ou fermé. Contactez-nous pour en savoir plus.';
      } else if (errorMsg.includes('user not found')) {
        userFriendlyMessage = 'Aucun compte trouvé avec cet email.';
      } else if (errorMsg.includes('weak password') || errorMsg.includes('password should be')) {
        userFriendlyMessage = 'Le mot de passe doit contenir au moins 8 caractères, dont une lettre et un chiffre.';
      } else if (errorMsg.includes('already registered') || errorMsg.includes('already been registered')) {
        userFriendlyMessage = 'Un compte existe déjà avec cet email : connectez-vous, ou utilisez « Mot de passe oublié ».';
      } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
        userFriendlyMessage = 'Problème de connexion. Vérifiez votre connexion internet.';
      } else if (errorMsg.includes('rate limit') || errorMsg.includes('too many')) {
        userFriendlyMessage = 'Trop de tentatives. Veuillez patienter quelques minutes.';
      } else {
        userFriendlyMessage = error.message;
      }
    }

    console.warn(`Authentification refusée ${context} :`, error?.message);
    setError(userFriendlyMessage);
    return userFriendlyMessage;
  }, []);

  /** Erreur transmise aux pages, avec un code exploitable (ex. email_not_confirmed) */
  const authFailure = useCallback((error, context) => {
    const failure = new Error(handleAuthError(error, context));
    const raw = String(error?.message || '').toLowerCase();
    if (raw.includes('email not confirmed') || raw.includes('email_not_confirmed')) failure.code = 'email_not_confirmed';
    if (raw.includes('account_suspended')) failure.code = 'account_suspended';
    return failure;
  }, [handleAuthError]);

  /**
   * Connexion utilisateur avec gestion d'erreurs améliorée
   */
  const signIn = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      setAuthAction('signin');

      if (isDevelopmentMode) {
        // Démonstration : seuls les comptes de démonstration existants, avec leur mot de passe exact
        const account = await checkDemoAccount(email, password);

        // Délai court, comme une vraie requête
        await new Promise(resolve => setTimeout(resolve, 600));

        // Compte suspendu ou fermé par l'administrateur : connexion refusée
        if (account && ['suspended', 'closed'].includes(account.status)) {
          throw new Error('account_suspended');
        }

        if (account) {
          const firstName = account.metadata?.first_name || email.split('@')[0].charAt(0).toUpperCase() + email.split('@')[0].slice(1);
          const lastName = account.metadata?.last_name || '';
          const mockUser = {
            id: demoUserId(email),
            email: normalizeEmail(email),
            user_metadata: {
              ...(account.metadata || {}),
              first_name: firstName,
              last_name: lastName,
              full_name: `${firstName} ${lastName}`.trim()
            },
            email_confirmed_at: new Date().toISOString(),
            created_at: new Date().toISOString()
          };

          const mockSession = {
            user: mockUser,
            access_token: 'dev-token-' + Date.now(),
            refresh_token: 'dev-refresh-' + Date.now(),
            expires_at: Date.now() + (60 * 60 * 1000) // 1 heure
          };

          setUser(mockUser);
          setSession(mockSession);
          setLoading(false);
          setAuthAction(null);
          return { user: mockUser, session: mockSession };
        } else {
          throw new Error('Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.');
        }
      }

      console.log('✅ Utilisation de Supabase réel pour l\'authentification');
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password
      });

      // Email non confirmé : la connexion est refusée (la page propose de renvoyer l'email de confirmation)
      if (error) throw error;

      // Compte suspendu ou fermé par l'administrateur : session fermée aussitôt
      const { data: profile } = await supabase.from('profiles').select('account_status').eq('id', data.user.id).maybeSingle();
      if (['suspended', 'closed'].includes(profile?.account_status)) {
        await supabase.auth.signOut();
        throw new Error('account_suspended');
      }

      // Les états seront mis à jour par le listener onAuthStateChange
      return data;

    } catch (error) {
      throw authFailure(error, 'lors de la connexion');
    } finally {
      setLoading(false);
      setAuthAction(null);
    }
  }, [authFailure]);

  /**
   * Connexion par lien magique (OTP) sans mot de passe
   */
  const signInWithMagicLink = useCallback(async (email, redirectTo = `${window.location.origin}/dashboard`) => {
    try {
      setLoading(true);
      setError(null);
      setAuthAction('signin');

      if (isDevelopmentMode) {
        // Démonstration : connexion directe, uniquement pour un compte existant
        if (!demoAccountExists(email)) {
          throw new Error('user not found');
        }
        const mockUser = {
          id: demoUserId(email),
          email: email.trim().toLowerCase(),
          email_confirmed_at: new Date().toISOString(),
        };
        const mockSession = {
          user: mockUser,
          access_token: 'dev-token-' + Date.now(),
          refresh_token: 'dev-refresh-' + Date.now(),
          expires_at: Date.now() + (60 * 60 * 1000)
        };
        setUser(mockUser);
        setSession(mockSession);
        return { success: true, magicLinkSimulated: true };
      }

      const { data, error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { emailRedirectTo: redirectTo }
      });

      if (error) {
        throw error;
      }

      // Un email est envoyé; l'utilisateur doit cliquer le lien
      return { success: true, magicLinkSent: true, data };
    } catch (error) {
      const errorMsg = handleAuthError(error, 'lors de l\'envoi du lien magique');
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
      setAuthAction(null);
    }
  }, [handleAuthError]);

  /**
   * Inscription utilisateur avec métadonnées
   */
  const signUp = useCallback(async (email, password, metadata = {}) => {
    try {
      setLoading(true);
      setError(null);
      setAuthAction('signup');

      if (isDevelopmentMode) {
        // Démonstration : le compte est enregistré (empreinte du mot de passe), puis l'utilisateur est connecté
        if (demoAccountExists(email)) {
          throw new Error('email already registered');
        }
        const firstName = metadata.first_name || email.split('@')[0].charAt(0).toUpperCase() + email.split('@')[0].slice(1);
        const lastName = metadata.last_name || '';
        const accounts = readDemoAccounts();
        accounts[normalizeEmail(email)] = {
          hash: await hashPassword(email, password),
          metadata: { ...metadata, first_name: firstName, last_name: lastName },
          created_at: new Date().toISOString()
        };
        writeDemoAccounts(accounts);
        await new Promise(resolve => setTimeout(resolve, 600));

        const mockUser = {
          id: demoUserId(email),
          email: normalizeEmail(email),
          user_metadata: {
            ...metadata,
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`.trim()
          },
          email_confirmed_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        };

        const mockSession = {
          user: mockUser,
          access_token: 'dev-token-' + Date.now(),
          refresh_token: 'dev-refresh-' + Date.now(),
          expires_at: Date.now() + (60 * 60 * 1000) // 1 heure
        };

        setUser(mockUser);
        setSession(mockSession);
        setLoading(false);
        setAuthAction(null);

        return { user: mockUser, session: mockSession }; // Connexion automatique
      }

      // Mode production avec Supabase réel
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password: password,
        options: {
          data: {
            first_name: metadata.first_name || '',
            last_name: metadata.last_name || '',
            ...metadata
          },
          // Le lien de confirmation ramène sur la page de connexion
          emailRedirectTo: `${window.location.origin}/login?confirme=1`
        }
      });

      if (error) {
        throw error;
      }

      // Sans session : l'adresse doit d'abord être confirmée (lien envoyé par email)
      return data;

    } catch (error) {
      throw authFailure(error, 'lors de l\'inscription');
    } finally {
      setLoading(false);
      setAuthAction(null);
    }
  }, [authFailure]);

  /** Renvoie l'email de confirmation d'inscription */
  const resendConfirmation = useCallback(async (email) => {
    if (isDevelopmentMode) return { success: true, demo: true };
    const { error: resendError } = await supabase.auth.resend({
      type: 'signup',
      email: normalizeEmail(email),
      options: { emailRedirectTo: `${window.location.origin}/login?confirme=1` }
    });
    if (resendError) return { success: false, error: handleAuthError(resendError, 'lors du renvoi de la confirmation') };
    return { success: true };
  }, [handleAuthError]);

  /**
   * Déconnexion utilisateur
   */
  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAuthAction('signout');

      if (isDevelopmentMode) {
        console.log('🔧 Mode développement : simulation de déconnexion');
        await new Promise(resolve => setTimeout(resolve, 500));

        setUser(null);
        setSession(null);
        setLoading(false);
        setAuthAction(null);
        return;
      }

      const { error } = await supabase.auth.signOut();

      if (error) {
        throw error;
      }

      // Les états seront mis à jour par le listener

    } catch (error) {
      handleAuthError(error, 'lors de la déconnexion');
    } finally {
      setLoading(false);
      setAuthAction(null);
    }
  }, [handleAuthError]);

  /**
   * Réinitialisation du mot de passe
   */
  const resetPassword = useCallback(async (email) => {
    try {
      setError(null);

      if (isDevelopmentMode) {
        console.log('🔧 Mode développement : simulation de reset password');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return { data: null, error: null };
      }

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        throw error;
      }

      return { data, error: null };

    } catch (error) {
      const errorMsg = handleAuthError(error, 'lors de la réinitialisation');
      return { data: null, error: errorMsg };
    }
  }, [handleAuthError]);

  /**
   * Actualisation de la session
   */
  const refreshSession = useCallback(async () => {
    try {
      if (isDevelopmentMode) {
        console.log('🔧 Mode développement : refresh session simulé');
        return;
      }

      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        console.warn('Erreur lors du refresh de session:', error.message);
      }

      return data;
    } catch (error) {
      console.warn('Erreur inattendue lors du refresh:', error.message);
    }
  }, []);

  /**
   * Initialisation et listeners d'authentification
   */
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        if (isDevelopmentMode) {
          console.log('🔧 Mode développement : initialisation auth simulée');
          const savedSession = readDemoSession();
          setUser(savedSession?.user ?? null);
          setSession(savedSession);
          setLoading(false);
          setIsInitialized(true);
          return;
        }

        // Récupérer la session initiale
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Erreur lors de la récupération de la session:', error.message);
          if (error.message.includes('Invalid Refresh Token')) {
            // Effacer le token invalide
            const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
            const ref = new URL(supabaseUrl).hostname.split('.')[0];
            const storageKey = `sb-${ref}-auth-token`;
            localStorage.removeItem(storageKey);
            console.log('Token invalide effacé, nouvelle tentative de récupération de session.');

            // Nouvelle tentative
            const { data: { session: newSession }, error: retryError } = await supabase.auth.getSession();

            if (retryError) {
              console.error('Erreur lors de la seconde tentative:', retryError.message);
              setError(retryError.message);
            } else if (mounted) {
              setSession(newSession);
              setUser(newSession?.user ?? null);
            }
          } else {
            setError(error.message);
          }
        } else if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
        }

      } catch (error) {
        if (mounted) {
          console.error('Erreur inattendue lors de l\'initialisation:', error.message);
          setError(error.message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setIsInitialized(true);
        }
      }
    };

    // Listener pour les changements d'état d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;


        switch (event) {
          case 'SIGNED_IN':
            setSession(session);
            setUser(session?.user ?? null);
            setError(null);
            break;

          case 'SIGNED_OUT':
            setSession(null);
            setUser(null);
            setError(null);
            break;

          // Double vérification réussie (administrateur) : nouvelle session « aal2 » à transmettre au serveur de paiement
          case 'TOKEN_REFRESHED':
          case 'MFA_CHALLENGE_VERIFIED':
            setSession(session);
            setUser(session?.user ?? null);
            break;

          case 'USER_UPDATED':
            setUser(session?.user ?? null);
            break;

          default:
            break;
        }

        setLoading(false);
      }
    );

    // Initialiser l'authentification
    initializeAuth();

    // Nettoyage à la destruction du composant
    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  /**
   * Propriétés calculées pour faciliter l'usage
   * Mémorisées pour éviter les re-renders inutiles
   */
  const isAuthenticated = !!user;
  const isEmailConfirmed = user?.email_confirmed_at ? true : false;

  const userMetadata = useMemo(() =>
    user?.user_metadata || {}
  , [user?.user_metadata]);

  const userDisplayName = useMemo(() =>
    userMetadata.first_name
      ? `${userMetadata.first_name} ${userMetadata.last_name || ''}`.trim()
      : user?.email?.split('@')[0] || 'Utilisateur'
  , [userMetadata.first_name, userMetadata.last_name, user?.email]);

  /**
   * Mise à jour des informations de profil (métadonnées utilisateur).
   * Accepte les noms historiques des pages (prenom, nom, telephone, ville, photo),
   * ainsi que preferences (paramètres) et paymentMethods (moyens de paiement enregistrés).
   */
  const updateUser = useCallback(async (updates = {}) => {
    const metadata = {
      ...(updates.prenom !== undefined && { first_name: updates.prenom }),
      ...(updates.nom !== undefined && { last_name: updates.nom }),
      ...(updates.telephone !== undefined && { phone: updates.telephone }),
      ...(updates.ville !== undefined && { city: updates.ville }),
      ...(updates.photo !== undefined && { avatar_url: updates.photo }),
      ...(updates.preferences !== undefined && { preferences: updates.preferences }),
      ...(updates.paymentMethods !== undefined && { payment_methods: updates.paymentMethods })
    };
    if (metadata.first_name !== undefined || metadata.last_name !== undefined) {
      metadata.full_name = `${metadata.first_name ?? userMetadata.first_name ?? ''} ${metadata.last_name ?? userMetadata.last_name ?? ''}`.trim();
    }

    if (isDevelopmentMode) {
      const updatedUser = { ...user, user_metadata: { ...(user?.user_metadata || {}), ...metadata } };
      // Conserver les informations du compte pour la prochaine connexion
      const key = normalizeEmail(user?.email);
      const accounts = readDemoAccounts();
      if (accounts[key]) {
        accounts[key] = { ...accounts[key], metadata: { ...(accounts[key].metadata || {}), ...metadata } };
        writeDemoAccounts(accounts);
      }
      setUser(updatedUser);
      setSession(prev => (prev ? { ...prev, user: updatedUser } : prev));
      return { user: updatedUser };
    }

    const { data, error: updateError } = await supabase.auth.updateUser({ data: metadata });
    if (updateError) throw updateError;
    setUser(data.user);
    return { user: data.user };
  }, [user, userMetadata.first_name, userMetadata.last_name]);

  /**
   * Nouveau mot de passe (après lien de réinitialisation)
   */
  const updatePassword = useCallback(async (newPassword) => {
    if (isDevelopmentMode) {
      // Démonstration : nouveau mot de passe du compte connecté (les comptes fournis ne sont pas modifiables)
      const key = normalizeEmail(user?.email);
      const accounts = readDemoAccounts();
      if (accounts[key]) {
        accounts[key] = { ...accounts[key], hash: await hashPassword(key, newPassword) };
        writeDemoAccounts(accounts);
      }
      return { success: true };
    }
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) throw updateError;
    return { success: true };
  }, [user]);

  /**
   * Changement de mot de passe depuis les paramètres : vérifie d'abord le mot de passe actuel
   */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const email = normalizeEmail(user?.email);
    if (isDevelopmentMode) {
      if (!(await checkDemoAccount(email, currentPassword))) throw new Error('Mot de passe actuel incorrect.');
      if (BUILT_IN_DEMO_ACCOUNTS[email] !== undefined) throw new Error('Le mot de passe des comptes de démonstration fournis ne peut pas être modifié.');
      return updatePassword(newPassword);
    }
    const { error: checkError } = await supabase.auth.signInWithPassword({ email, password: currentPassword });
    if (checkError) throw new Error('Mot de passe actuel incorrect.');
    return updatePassword(newPassword);
  }, [user, updatePassword]);

  /**
   * Vue "profil" de l'utilisateur avec les noms de champs utilisés par les pages historiques
   */
  const currentUser = useMemo(() => {
    if (!user) return null;
    const firstName = userMetadata.first_name || '';
    const lastName = userMetadata.last_name || '';
    return {
      ...user,
      name: userDisplayName,
      prenom: firstName,
      nom: lastName,
      telephone: userMetadata.phone || '',
      ville: userMetadata.city || '',
      photo: userMetadata.avatar_url || '',
      dateInscription: user.created_at
    };
  }, [user, userMetadata, userDisplayName]);

  /**
   * Valeur du contexte avec toutes les fonctionnalités
   * Mémorisée pour éviter les re-renders inutiles
   */
  const contextValue = useMemo(() => ({
    // États de base
    user,
    session,
    loading,
    error,

    // États étendus
    isAuthenticated,
    isEmailConfirmed,
    isInitialized,
    authAction,

    // Métadonnées utilisateur
    userMetadata,
    userDisplayName,

    // Méthodes d'authentification
    signIn,
    signInWithMagicLink,
    signUp,
    signOut,
    resetPassword,
    resendConfirmation,
    refreshSession,

    // Profil et alias utilisés par les pages (harmonisation)
    currentUser,
    updateUser,
    updatePassword,
    changePassword,
    logout: signOut,
    isLoading: loading,
    authLoading: loading,

    // Utilitaires
    clearError: () => setError(null),
    isDevelopmentMode
  }), [
    user,
    session,
    loading,
    error,
    isAuthenticated,
    isEmailConfirmed,
    isInitialized,
    authAction,
    userMetadata,
    userDisplayName,
    signIn,
    signInWithMagicLink,
    signUp,
    signOut,
    resetPassword,
    resendConfirmation,
    refreshSession,
    currentUser,
    updateUser,
    updatePassword,
    changePassword
  ]);

  return (
    <SupabaseAuthContext.Provider value={contextValue}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};

/**
 * Hook pour obtenir uniquement l'état de chargement
 */
export const useAuthLoading = () => {
  const { loading, isInitialized } = useAuth();
  return loading || !isInitialized;
};

/**
 * Hook pour obtenir uniquement l'utilisateur connecté
 */
export const useCurrentUser = () => {
  const { user, isAuthenticated, userDisplayName } = useAuth();
  return { user, isAuthenticated, userDisplayName };
};

/**
 * Hook pour obtenir uniquement les méthodes d'authentification
 */
export const useAuthActions = () => {
  const { signIn, signUp, signOut, resetPassword } = useAuth();
  return { signIn, signUp, signOut, resetPassword };
};

export default SupabaseAuthProvider;